import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { storage } from "../../utils/storage";
import {
  DASHBOARD_TOUR_STORAGE_KEY,
  PAGE_TOUR_STEP_EVENT,
  PAGE_TOUR_STORAGE_KEY,
  PIVOT_TOUR_VARIANT,
  START_PAGE_TOUR_EVENT,
  STOP_PAGE_TOUR_EVENT,
  tourForPath,
} from "./pageTours";
import "./firstRunTour.css";

export {
  DASHBOARD_TOUR_STORAGE_KEY,
  PAGE_TOUR_STEP_EVENT,
  PIVOT_TOUR_VARIANT,
  START_PAGE_TOUR_EVENT,
  STOP_PAGE_TOUR_EVENT,
};
export const START_DASHBOARD_TOUR_EVENT = START_PAGE_TOUR_EVENT;

const readSeenMap = () => {
  const raw = storage.get(PAGE_TOUR_STORAGE_KEY, {});
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
};

const storageKeyFor = (pathname, variant) =>
  variant ? `${pathname}::${variant}` : pathname;

const hasSeenKey = (key) => {
  if (!key) return true;
  if (readSeenMap()[key]) return true;
  if (key === "/dashboard") {
    return Boolean(storage.get(DASHBOARD_TOUR_STORAGE_KEY, false));
  }
  return false;
};

const markKeySeen = (key) => {
  if (!key) return;
  storage.set(PAGE_TOUR_STORAGE_KEY, { ...readSeenMap(), [key]: true });
  if (key === "/dashboard") {
    storage.set(DASHBOARD_TOUR_STORAGE_KEY, true);
  }
};

const clearKeySeen = (key) => {
  if (!key) return;
  const next = { ...readSeenMap() };
  delete next[key];
  storage.set(PAGE_TOUR_STORAGE_KEY, next);
  if (key === "/dashboard") {
    storage.set(DASHBOARD_TOUR_STORAGE_KEY, false);
  }
};

const readTarget = (selector) => {
  const node = document.querySelector(selector);
  if (!node) return null;
  const rect = node.getBoundingClientRect();
  if (rect.width < 2 && rect.height < 2) return null;
  return { node, rect };
};

const isPivotBuilderOpen = () => Boolean(document.querySelector(".pivot-table-wrapper"));

export const requestPageTour = () => {
  window.dispatchEvent(
    new CustomEvent(START_PAGE_TOUR_EVENT, {
      detail: {
        variant: isPivotBuilderOpen() ? PIVOT_TOUR_VARIANT : null,
        force: true,
      },
    })
  );
};

export const requestPivotTour = ({ force = false } = {}) => {
  window.dispatchEvent(
    new CustomEvent(START_PAGE_TOUR_EVENT, {
      detail: { variant: PIVOT_TOUR_VARIANT, force },
    })
  );
};

export const stopPageTour = ({ markSeen = false } = {}) => {
  window.dispatchEvent(new CustomEvent(STOP_PAGE_TOUR_EVENT, { detail: { markSeen } }));
};

export const requestDashboardTour = requestPageTour;

const FirstRunTour = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const [variant, setVariant] = useState(null);
  const variantRef = useRef(null);
  variantRef.current = variant;
  const steps = useMemo(() => tourForPath(pathname, variant), [pathname, variant]);
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const closeTour = useCallback(
    (markSeen = true) => {
      if (markSeen) markKeySeen(storageKeyFor(pathname, variantRef.current));
      setActive(false);
      setStepIndex(0);
      setRect(null);
      setVariant(null);
    },
    [pathname]
  );

  useEffect(() => {
    const onStart = (event) => {
      const nextVariant = event.detail?.variant || null;
      const force = Boolean(event.detail?.force);
      const key = storageKeyFor(pathname, nextVariant);
      if (!force && hasSeenKey(key)) return;
      if (force) clearKeySeen(key);
      setVariant(nextVariant);
      setStepIndex(0);
      setActive(true);
    };
    const onStop = (event) => {
      closeTour(Boolean(event.detail?.markSeen));
    };
    window.addEventListener(START_PAGE_TOUR_EVENT, onStart);
    window.addEventListener(STOP_PAGE_TOUR_EVENT, onStop);
    return () => {
      window.removeEventListener(START_PAGE_TOUR_EVENT, onStart);
      window.removeEventListener(STOP_PAGE_TOUR_EVENT, onStop);
    };
  }, [pathname, closeTour]);

  useEffect(() => {
    setActive(false);
    setStepIndex(0);
    setRect(null);
    setVariant(null);
    if (hasSeenKey(pathname)) return undefined;
    const timer = window.setTimeout(() => {
      if (!hasSeenKey(pathname)) setActive(true);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  const step = steps[Math.min(stepIndex, steps.length - 1)] || steps[0];

  useEffect(() => {
    if (!active || !step) return undefined;
    window.dispatchEvent(
      new CustomEvent(PAGE_TOUR_STEP_EVENT, {
        detail: { id: step.id, variant: variantRef.current },
      })
    );
  }, [active, step]);

  useEffect(() => {
    if (!active || !step) return undefined;
    let attempts = 0;
    const measure = () => {
      const found = readTarget(step.target);
      setRect(found?.rect || null);
      return Boolean(found);
    };
    measure();
    const retry = window.setInterval(() => {
      attempts += 1;
      const found = measure();
      if (found || attempts >= 16) {
        window.clearInterval(retry);
        readTarget(step.target)?.node?.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
        measure();
      }
    }, 120);
    const timer = window.setTimeout(() => {
      readTarget(step.target)?.node?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
      measure();
    }, 50);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.clearInterval(retry);
      window.clearTimeout(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, step]);

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeTour(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active, closeTour]);

  if (!active || !step) return null;

  const isLast = stepIndex >= steps.length - 1;
  const pad = 8;
  const body = rect ? step.body : step.fallback || step.body;
  const tooltipStyle = rect
    ? {
        top: Math.min(rect.bottom + 16, window.innerHeight - 230),
        left: Math.min(Math.max(rect.left, 16), window.innerWidth - 360),
      }
    : { top: 96, left: 24 };

  return createPortal(
    <div className="first-run-tour" role="dialog" aria-modal="true" aria-labelledby="first-run-tour-title">
      <button
        type="button"
        className="first-run-tour__dim"
        aria-label="Skip walkthrough"
        onClick={() => closeTour(true)}
      />
      {rect && (
        <div
          className="first-run-tour__spot"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
          }}
        />
      )}
      <div className="first-run-tour__card" style={tooltipStyle}>
        <p className="first-run-tour__progress">
          {stepIndex + 1} of {steps.length}
        </p>
        <h2 id="first-run-tour-title">{step.title}</h2>
        <p>{body}</p>
        <div className="first-run-tour__actions">
          <button type="button" className="first-run-tour__skip" onClick={() => closeTour(true)}>
            Skip
          </button>
          <div className="first-run-tour__nav">
            {stepIndex > 0 && (
              <button type="button" onClick={() => setStepIndex((index) => index - 1)}>
                Back
              </button>
            )}
            <button
              type="button"
              className="first-run-tour__next"
              onClick={() => {
                if (isLast) {
                  closeTour(true);
                  return;
                }
                setStepIndex((index) => index + 1);
              }}
            >
              {isLast ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FirstRunTour;
